<ng-container *transloco="let t">
  @if (isCiamEnabled()) {
    <div id="register-card" class="register-card--ciam flex flex-col w-full max-w-[480px] max-sm:max-w-full min-w-0">
      <div class="bg-white p-14 max-sm:px-4 max-sm:py-8 flex flex-col gap-6 max-sm:gap-4" id="register-card-inner">
        <div id="register-header" class="flex flex-col items-start gap-6 max-sm:gap-4">
          <img id="register-logo" [src]="logoUrl()" [alt]="partnerDisplayName()" class="h-14 max-sm:h-10 w-auto" />
          @if (!accountCreated()) {
            <h1 id="register-title" class="register-card__ciam-heading m-0">
              {{
                nativeAuthStep() === 'signin-mfa-verify'
                  ? t('authRegisterSigninMfaCiamTitle')
                  : emailVerified() && !accountCreated()
                    ? t('authRegisterCiamCreatePasswordTitle')
                    : t('authRegisterCiamTitle')
              }}
            </h1>
          }
        </div>

        @if (errorMessage()) {
          <div
            id="register-error-message"
            class="bg-ld-error-light border border-ld-error rounded-lg px-4 py-3 text-ld-error text-sm"
            role="alert"
          >
            {{ errorMessage() }}
          </div>
        }

        <form id="register-form" [formGroup]="registerForm" (ngSubmit)="onSubmit()" class="flex flex-col gap-4">
          @if (!emailVerified() && !codeSent()) {
            <mat-form-field id="first-name-field" appearance="fill" class="w-full register-card__ciam-input">
              <input
                matInput
                #firstNameInput
                id="first-name-input"
                type="text"
                formControlName="firstName"
                aria-required="true"
                [placeholder]="t('authRegisterEnterFirstName')"
                [attr.aria-invalid]="registerForm.get('firstName')?.invalid && registerForm.get('firstName')?.touched"
              />
              <mat-error id="first-name-error">{{ t('commonCommonErrorsFirstNameRequired') }}</mat-error>
            </mat-form-field>

            <mat-form-field id="last-name-field" appearance="fill" class="w-full register-card__ciam-input">
              <input
                matInput
                id="last-name-input"
                type="text"
                formControlName="lastName"
                aria-required="true"
                [placeholder]="t('authRegisterEnterLastName')"
                [attr.aria-invalid]="registerForm.get('lastName')?.invalid && registerForm.get('lastName')?.touched"
              />
              <mat-error id="last-name-error">{{ t('commonCommonErrorsLastNameRequired') }}</mat-error>
            </mat-form-field>

            <mat-form-field id="email-field" appearance="fill" class="w-full register-card__ciam-input">
              <input
                matInput
                id="register-email-input"
                type="email"
                formControlName="email"
                aria-required="true"
                [placeholder]="t('authRegisterEnterEmailAddress')"
                [attr.aria-invalid]="registerForm.get('email')?.invalid && registerForm.get('email')?.touched"
              />
              <mat-error id="register-email-error">{{ t('commonValidationInvalidEmail') }}</mat-error>
            </mat-form-field>
            @if (emailError()) {
              <p id="register-email-lookup-error" class="text-ld-error text-sm m-0 -mt-2" role="alert">
                {{ emailError() }}
              </p>
            }

            <div class="flex justify-end mt-2">
              <button
                type="submit"
                id="send-code-button"
                class="register-card__ciam-btn text-white border-0 cursor-pointer transition-all hover:brightness-90 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 flex items-center justify-center gap-2"
                [disabled]="sendingCode() || isCreateAccountStepInvalid()"
              >
                {{ sendingCode() ? t('authRegisterNativeAuthSending') : t('authRegisterNativeAuthSendCodeButton') }}
              </button>
            </div>
          }

          @if (!emailVerified() && codeSent()) {
            <p id="native-auth-email-display" class="register-card__ciam-text m-0 font-semibold">
              {{ registerForm.getRawValue().email }}
            </p>

            <p id="native-auth-verify-instruction" class="register-card__ciam-text m-0">
              {{ t('authRegisterCiamVerifyCodeInstruction') }}
            </p>

            <mat-form-field id="otp-field" appearance="fill" class="w-full register-card__ciam-input">
              <input
                matInput
                #otpInput
                id="otp-input"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                [formControl]="otpControl"
                aria-required="true"
                [placeholder]="t('authRegisterCiamEnterCode')"
                [attr.aria-invalid]="otpControl.invalid && otpControl.touched"
              />
              <mat-error id="otp-error">{{ t('authRegisterNativeAuthOtpRequired') }}</mat-error>
            </mat-form-field>

            <button
              type="button"
              id="resend-code-button"
              (click)="onResendCode()"
              class="register-card__ciam-link bg-transparent border-0 p-0 cursor-pointer hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:rounded self-start"
              [disabled]="sendingCode()"
            >
              {{ t('authRegisterCiamResendCode') }}
            </button>

            <div class="flex justify-end">
              <button
                type="submit"
                id="verify-code-button"
                class="register-card__ciam-btn text-white border-0 cursor-pointer transition-all hover:brightness-90 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 flex items-center justify-center gap-2"
                [disabled]="verifyingCode() || isVerifyCodeStepInvalid()"
              >
                {{ verifyingCode() ? t('authRegisterNativeAuthVerifying') : t('authRegisterCiamVerifyButton') }}
              </button>
            </div>
          }

          @if (nativeAuthStep() === 'signin-mfa-verify') {
            <p id="signin-mfa-instruction" class="register-card__ciam-text m-0">
              {{ t('authRegisterSigninMfaCiamInstruction') }}
            </p>

            <mat-form-field id="signin-mfa-otp-field" appearance="fill" class="w-full register-card__ciam-input">
              <input
                matInput
                #signinMfaOtpInput
                id="signin-mfa-otp-input"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                [formControl]="signinMfaOtpControl"
                aria-required="true"
                [placeholder]="t('authRegisterSigninMfaOtpLabel')"
                [attr.aria-invalid]="signinMfaOtpControl.invalid && signinMfaOtpControl.touched"
              />
              <mat-error id="signin-mfa-otp-error">{{ t('authRegisterSigninMfaOtpRequired') }}</mat-error>
            </mat-form-field>

            <div class="flex justify-end">
              <button
                type="button"
                id="signin-mfa-verify-button"
                (click)="onSigninMfaVerify()"
                class="register-card__ciam-btn text-white border-0 cursor-pointer transition-all hover:brightness-90 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 flex items-center justify-center gap-2"
                [disabled]="verifyingSigninMfa() || signinMfaOtpControl.invalid"
                [attr.aria-busy]="verifyingSigninMfa()"
              >
                {{
                  verifyingSigninMfa() ? t('authRegisterSigninMfaVerifying') : t('authRegisterSigninMfaVerifyButton')
                }}
              </button>
            </div>
          }

          @if (emailVerified() && !accountCreated() && nativeAuthStep() !== 'signin-mfa-verify') {
            <p id="native-auth-email-display" class="register-card__ciam-text m-0 font-semibold">
              {{ registerForm.getRawValue().email }}
            </p>

            <mat-form-field id="password-field" appearance="fill" class="w-full register-card__ciam-input">
              <input
                matInput
                #passwordInput
                id="register-password-input"
                [type]="hidePassword() ? 'password' : 'text'"
                formControlName="password"
                aria-required="true"
                glance_masked="true"
                [placeholder]="t('authRegisterCiamEnterPassword')"
                [attr.aria-invalid]="registerForm.get('password')?.invalid && registerForm.get('password')?.touched"
              />
              <button
                type="button"
                matSuffix
                id="register-password-toggle"
                (click)="togglePasswordVisibility()"
                class="bg-transparent border-0 cursor-pointer p-0 pr-2 text-ld-neutral-500 flex items-center justify-center hover:text-ld-neutral"
                [attr.aria-label]="hidePassword() ? t('commonAriaShowPassword') : t('commonAriaHidePassword')"
              >
                <mat-icon>{{ hidePassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
              </button>
              @if (registerForm.get('password')?.hasError('required')) {
                <mat-error id="register-password-error-required">{{ t('authRegisterPasswordRequired') }}</mat-error>
              } @else if (registerForm.get('password')?.hasError('minlength')) {
                <mat-error id="register-password-error-length">
                  {{ t('commonValidationMinLength', { min: 8 }) }}
                </mat-error>
              } @else if (registerForm.get('password')?.hasError('passwordStrength')) {
                <mat-error id="register-password-error-strength">{{ t('authRegisterPasswordStrength') }}</mat-error>
              }
            </mat-form-field>

            <div
              id="password-rules"
              class="register-card__ciam-password-rules"
              role="list"
              [attr.aria-label]="t('authRegisterCiamPasswordMustContain')"
            >
              <p role="presentation" class="register-card__ciam-text m-0 col-span-2">
                {{ t('authRegisterCiamPasswordMustContain') }}
              </p>
              <span
                role="listitem"
                class="register-card__ciam-password-rule"
                [class.register-card__ciam-password-rule--pass]="passwordHasMinLength()"
                [class.register-card__ciam-password-rule--fail]="passwordValue().length > 0 && !passwordHasMinLength()"
              >
                <mat-icon class="register-card__ciam-password-rule-icon">
                  {{ passwordHasMinLength() ? 'check' : 'close' }}
                </mat-icon>
                {{ t('authRegisterCiamRule8Chars') }}
              </span>
              <span
                role="listitem"
                class="register-card__ciam-password-rule"
                [class.register-card__ciam-password-rule--pass]="passwordHasSpecialChar()"
                [class.register-card__ciam-password-rule--fail]="
                  passwordValue().length > 0 && !passwordHasSpecialChar()
                "
              >
                <mat-icon class="register-card__ciam-password-rule-icon">
                  {{ passwordHasSpecialChar() ? 'check' : 'close' }}
                </mat-icon>
                {{ t('authRegisterCiamRuleSpecialChar') }}
              </span>
              <span
                role="listitem"
                class="register-card__ciam-password-rule"
                [class.register-card__ciam-password-rule--pass]="passwordHasLetter()"
                [class.register-card__ciam-password-rule--fail]="passwordValue().length > 0 && !passwordHasLetter()"
              >
                <mat-icon class="register-card__ciam-password-rule-icon">
                  {{ passwordHasLetter() ? 'check' : 'close' }}
                </mat-icon>
                {{ t('authRegisterCiamRuleLetterChar') }}
              </span>
              <span
                role="listitem"
                class="register-card__ciam-password-rule"
                [class.register-card__ciam-password-rule--pass]="passwordHasNumber()"
                [class.register-card__ciam-password-rule--fail]="passwordValue().length > 0 && !passwordHasNumber()"
              >
                <mat-icon class="register-card__ciam-password-rule-icon">
                  {{ passwordHasNumber() ? 'check' : 'close' }}
                </mat-icon>
                {{ t('authRegisterCiamRuleNumber') }}
              </span>
            </div>

            <mat-form-field id="confirm-password-field" appearance="fill" class="w-full register-card__ciam-input">
              <input
                matInput
                id="register-confirm-password-input"
                [type]="hideConfirmPassword() ? 'password' : 'text'"
                formControlName="confirmPassword"
                aria-required="true"
                glance_masked="true"
                [placeholder]="t('authRegisterCiamConfirmPassword')"
                [errorStateMatcher]="confirmPasswordErrorMatcher"
                [attr.aria-invalid]="
                  (registerForm.get('confirmPassword')?.invalid || registerForm.hasError('passwordMismatch')) &&
                  registerForm.get('confirmPassword')?.touched
                "
              />
              <button
                type="button"
                matSuffix
                id="register-confirm-password-toggle"
                (click)="toggleConfirmPasswordVisibility()"
                class="bg-transparent border-0 cursor-pointer p-0 pr-2 text-ld-neutral-500 flex items-center justify-center hover:text-ld-neutral"
                [attr.aria-label]="hideConfirmPassword() ? t('commonAriaShowPassword') : t('commonAriaHidePassword')"
              >
                <mat-icon>{{ hideConfirmPassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
              </button>
              @if (registerForm.get('confirmPassword')?.hasError('required')) {
                <mat-error id="register-confirm-password-error-required">
                  {{ t('authRegisterConfirmPasswordRequired') }}
                </mat-error>
              } @else if (registerForm.hasError('passwordMismatch')) {
                <mat-error id="register-confirm-password-error-mismatch">
                  {{ t('authRegisterCiamPasswordMismatch') }}
                </mat-error>
              }
            </mat-form-field>

            <div class="flex justify-end">
              <button
                type="submit"
                id="register-submit-button"
                class="register-card__ciam-btn text-white border-0 cursor-pointer transition-all hover:brightness-90 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 flex items-center justify-center gap-2"
                [disabled]="loading() || isCreatePasswordStepInvalid()"
                [attr.aria-busy]="loading()"
              >
                {{ loading() ? t('authRegisterCreating') : t('authRegisterCiamCreateAccountButton') }}
              </button>
            </div>
          }

          @if (accountCreated()) {
            <h2 id="welcome-title" class="register-card__ciam-heading m-0">
              {{ t('authRegisterCiamWelcomeTitle') }}
            </h2>
            <p id="welcome-description" class="register-card__ciam-text m-0">
              {{ t('authRegisterCiamWelcomeDescription') }}
            </p>
            <div class="flex justify-end">
              <button
                type="button"
                id="continue-to-sign-in-button"
                (click)="onContinueToSignIn()"
                class="register-card__ciam-btn text-white border-0 cursor-pointer transition-all hover:brightness-90 active:scale-[0.98] disabled:opacity-70 disabled:cursor-wait focus-visible:outline-2 focus-visible:outline-offset-2 flex items-center justify-center gap-2"
                [disabled]="loading()"
                [attr.aria-busy]="loading()"
              >
                {{ loading() ? t('authRegisterCiamRedirecting') : t('authRegisterCiamContinueToSignIn') }}
              </button>
            </div>
          }

          @if (!accountCreated()) {
            <p id="register-login-link" class="register-card__ciam-text flex items-center justify-end gap-1 m-0">
              <span>{{ t('authRegisterAlreadyRegistered') }}</span>
              <button
                type="button"
                id="back-to-login-text-button"
                class="register-card__ciam-link bg-transparent border-0 p-0 cursor-pointer hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:rounded"
                (click)="onBackToLogin()"
              >
                {{ t('authRegisterSignIn') }}
              </button>
            </p>
          }
        </form>
      </div>
      <footer id="register-footer" class="register-card__ciam-footer px-14 py-4 max-sm:px-4">
        <p class="m-0">{{ t('authRegisterCiamNmlsFooter') }}</p>
      </footer>
    </div>
  } @else {
    <div id="register-card" class="bg-white rounded-2xl shadow-card p-4 sm:p-10 w-full max-w-[480px] min-w-0">
      <div id="register-header" class="flex items-center gap-3 mb-6">
        <button
          type="button"
          id="back-to-login-button"
          (click)="onBackToLogin()"
          class="flex items-center justify-center w-10 h-10 rounded-full bg-transparent border-0 cursor-pointer hover:bg-ld-neutral-100 transition-colors focus-visible:outline-2 focus-visible:outline-ld-primary"
          [attr.aria-label]="t('authRegisterBackToLogin')"
        >
          <mat-icon class="w-6! h-6! text-[24px]! text-ld-neutral-500">arrow_back</mat-icon>
        </button>
        <h1 id="register-title" class="text-section-heading text-ld-neutral m-0">
          {{ t('authRegisterTitle') }}
        </h1>
      </div>

      @if (errorMessage()) {
        <div
          id="register-error-message"
          class="bg-ld-error-light border border-ld-error rounded-lg px-4 py-3 mb-6 text-ld-error text-sm"
          role="alert"
        >
          {{ errorMessage() }}
        </div>
      }

      <form id="register-form" [formGroup]="registerForm" (ngSubmit)="onSubmit()" class="flex flex-col gap-5">
        @if (!emailVerified() && !codeSent()) {
          <p id="native-auth-instruction" class="text-sm text-ld-neutral-500 m-0">
            {{ t('authRegisterNativeAuthSendCodeInstruction') }}
          </p>

          <mat-form-field id="email-field" appearance="outline" class="w-full">
            <mat-label>{{ t('commonFieldsEmail') }}</mat-label>
            <input
              matInput
              #registerEmailInput
              id="register-email-input"
              type="email"
              formControlName="email"
              aria-required="true"
              [attr.aria-invalid]="registerForm.get('email')?.invalid && registerForm.get('email')?.touched"
            />
            <mat-error id="register-email-error">{{ t('commonValidationInvalidEmail') }}</mat-error>
          </mat-form-field>

          <mat-form-field id="first-name-field" appearance="outline" class="w-full">
            <mat-label>{{ t('commonFirstName') }}</mat-label>
            <input
              matInput
              id="first-name-input"
              type="text"
              formControlName="firstName"
              aria-required="true"
              [attr.aria-invalid]="registerForm.get('firstName')?.invalid && registerForm.get('firstName')?.touched"
            />
            <mat-error id="first-name-error">{{ t('commonCommonErrorsFirstNameRequired') }}</mat-error>
          </mat-form-field>

          <mat-form-field id="last-name-field" appearance="outline" class="w-full">
            <mat-label>{{ t('commonLastName') }}</mat-label>
            <input
              matInput
              id="last-name-input"
              type="text"
              formControlName="lastName"
              aria-required="true"
              [attr.aria-invalid]="registerForm.get('lastName')?.invalid && registerForm.get('lastName')?.touched"
            />
            <mat-error id="last-name-error">{{ t('commonCommonErrorsLastNameRequired') }}</mat-error>
          </mat-form-field>

          <button
            type="submit"
            id="send-code-button"
            class="btn-primary-ld-purple !order-none !w-full"
            [disabled]="sendingCode() || isCreateAccountStepInvalid()"
          >
            {{ sendingCode() ? t('authRegisterNativeAuthSending') : t('authRegisterNativeAuthSendCodeButton') }}
          </button>
        }

        @if (!emailVerified() && codeSent()) {
          <p id="native-auth-verify-instruction" class="text-sm text-ld-neutral-500 m-0">
            {{ t('authRegisterNativeAuthVerifyCodeInstruction') }}
          </p>

          <mat-form-field id="otp-field" appearance="outline" class="w-full">
            <mat-label>{{ t('authRegisterNativeAuthOtpLabel') }}</mat-label>
            <input
              matInput
              #otpInput
              id="otp-input"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              [formControl]="otpControl"
              aria-required="true"
              [attr.aria-invalid]="otpControl.invalid && otpControl.touched"
            />
            <mat-error id="otp-error">{{ t('authRegisterNativeAuthOtpRequired') }}</mat-error>
          </mat-form-field>

          <button
            type="submit"
            id="verify-code-button"
            class="btn-primary-ld-purple !order-none !w-full"
            [disabled]="verifyingCode() || isVerifyCodeStepInvalid()"
          >
            {{ verifyingCode() ? t('authRegisterNativeAuthVerifying') : t('authRegisterNativeAuthVerifyCodeButton') }}
          </button>
        }

        @if (nativeAuthStep() === 'signin-mfa-verify') {
          <p id="signin-mfa-instruction" class="text-sm text-ld-neutral-500 m-0">
            {{ t('authRegisterSigninMfaInstruction') }}
          </p>

          <mat-form-field id="signin-mfa-otp-field" appearance="outline" class="w-full">
            <mat-label>{{ t('authRegisterSigninMfaOtpLabel') }}</mat-label>
            <input
              matInput
              #signinMfaOtpInput
              id="signin-mfa-otp-input"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              [formControl]="signinMfaOtpControl"
              aria-required="true"
              [attr.aria-invalid]="signinMfaOtpControl.invalid && signinMfaOtpControl.touched"
            />
            <mat-error id="signin-mfa-otp-error">{{ t('authRegisterSigninMfaOtpRequired') }}</mat-error>
          </mat-form-field>

          <button
            type="button"
            id="signin-mfa-verify-button"
            (click)="onSigninMfaVerify()"
            class="btn-primary-ld-purple !order-none !w-full"
            [disabled]="verifyingSigninMfa() || signinMfaOtpControl.invalid"
            [attr.aria-busy]="verifyingSigninMfa()"
          >
            {{ verifyingSigninMfa() ? t('authRegisterSigninMfaVerifying') : t('authRegisterSigninMfaVerifyButton') }}
          </button>
        }

        @if (emailVerified() && !accountCreated() && nativeAuthStep() !== 'signin-mfa-verify') {
          <p id="native-auth-password-instruction" class="text-sm text-ld-neutral-500 m-0">
            {{ t('authRegisterNativeAuthSetPasswordInstruction') }}
          </p>

          <mat-form-field id="password-field" appearance="outline" class="w-full">
            <mat-label>{{ t('commonFieldsPassword') }}</mat-label>
            <input
              matInput
              #passwordInput
              id="register-password-input"
              [type]="hidePassword() ? 'password' : 'text'"
              formControlName="password"
              aria-required="true"
              glance_masked="true"
              [attr.aria-invalid]="registerForm.get('password')?.invalid && registerForm.get('password')?.touched"
            />
            <button
              type="button"
              matSuffix
              id="register-password-toggle"
              (click)="togglePasswordVisibility()"
              class="bg-transparent border-0 cursor-pointer p-0 pr-2 text-ld-neutral-500 flex items-center justify-center hover:text-ld-neutral"
              [attr.aria-label]="hidePassword() ? t('commonAriaShowPassword') : t('commonAriaHidePassword')"
            >
              <mat-icon>{{ hidePassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            @if (registerForm.get('password')?.hasError('required')) {
              <mat-error id="register-password-error-required">{{ t('authRegisterPasswordRequired') }}</mat-error>
            } @else if (registerForm.get('password')?.hasError('minlength')) {
              <mat-error id="register-password-error-length">
                {{ t('commonValidationMinLength', { min: 8 }) }}
              </mat-error>
            } @else if (registerForm.get('password')?.hasError('passwordStrength')) {
              <mat-error id="register-password-error-strength">{{ t('authRegisterPasswordStrength') }}</mat-error>
            }
          </mat-form-field>

          <mat-form-field id="confirm-password-field" appearance="outline" class="w-full">
            <mat-label>{{ t('commonFieldsConfirmPassword') }}</mat-label>
            <input
              matInput
              id="confirm-password-input"
              [type]="hideConfirmPassword() ? 'password' : 'text'"
              formControlName="confirmPassword"
              aria-required="true"
              glance_masked="true"
              [errorStateMatcher]="confirmPasswordErrorMatcher"
              [attr.aria-invalid]="
                (registerForm.get('confirmPassword')?.invalid || registerForm.hasError('passwordMismatch')) &&
                registerForm.get('confirmPassword')?.touched
              "
            />
            <button
              type="button"
              matSuffix
              id="confirm-password-toggle"
              (click)="toggleConfirmPasswordVisibility()"
              class="bg-transparent border-0 cursor-pointer p-0 pr-2 text-ld-neutral-500 flex items-center justify-center hover:text-ld-neutral"
              [attr.aria-label]="hideConfirmPassword() ? t('commonAriaShowPassword') : t('commonAriaHidePassword')"
            >
              <mat-icon>{{ hideConfirmPassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            @if (registerForm.get('confirmPassword')?.hasError('required')) {
              <mat-error id="confirm-password-error-required">
                {{ t('authRegisterConfirmPasswordRequired') }}
              </mat-error>
            } @else if (registerForm.hasError('passwordMismatch')) {
              <mat-error id="confirm-password-error-mismatch">{{ t('commonValidationPasswordMismatch') }}</mat-error>
            }
          </mat-form-field>

          <div id="register-actions" class="flex flex-col gap-4 mt-2">
            <button
              type="submit"
              id="register-submit-button"
              class="btn-primary-ld-purple !order-none !w-full"
              [disabled]="loading() || isCreatePasswordStepInvalid()"
              [attr.aria-busy]="loading()"
            >
              {{ loading() ? t('authRegisterCreating') : t('authRegisterNativeAuthSetPasswordButton') }}
            </button>
          </div>
        }

        @if (accountCreated()) {
          <h2 id="welcome-title" class="text-lg font-semibold text-ld-neutral m-0">
            {{ t('authRegisterCiamWelcomeTitle') }}
          </h2>
          <p id="welcome-description" class="text-sm text-ld-neutral-500 m-0">
            {{ t('authRegisterCiamWelcomeDescription') }}
          </p>
          <div class="flex flex-col gap-4 mt-2">
            <button
              type="button"
              id="continue-to-sign-in-button"
              (click)="onContinueToSignIn()"
              class="btn-primary-ld-purple !order-none !w-full"
              [disabled]="loading()"
              [attr.aria-busy]="loading()"
            >
              {{ loading() ? t('authRegisterCiamRedirecting') : t('authRegisterCiamContinueToSignIn') }}
            </button>
          </div>
        }

        @if (!accountCreated()) {
          <p id="register-login-link" class="text-center text-sm text-ld-neutral-500 m-0">
            {{ t('authRegisterLoginPrompt') }}
            <button
              type="button"
              id="back-to-login-text-button"
              (click)="onBackToLogin()"
              class="bg-transparent border-0 text-ld-link cursor-pointer p-0 font-dm text-sm font-semibold hover:underline focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 focus-visible:rounded touch-target"
            >
              {{ t('authRegisterLoginButton') }}
            </button>
          </p>
        }
      </form>
    </div>
  }
</ng-container>
